import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MEETING_PROVIDERS, MEETING_PROVIDER_LABELS, type MeetingProvider } from '@goyone/shared';
import { api } from '../../lib/api';
import { useTenantProfile } from '../../lib/settings';
import { Icon } from '../../components/Icon';
import { ErrorState, PageHead } from '../../components/ui';

/**
 * إعدادات الاجتماعات.
 *
 * لا تكامل مع Zoom أو Meet — وهذا مذكور صراحةً في الشاشة لا مسكوتٌ عنه.
 *
 * التكامل الحقيقي يحتاج تسجيل تطبيق لدى كل مزوّد ومراجعته، ومفاتيح لكل
 * شركة على حدة. وغرفة الاجتماع الدائمة تؤدي الغرض العملي نفسه: رابط واحد
 * يُدرَج تلقائيًا في كل اجتماع، فلا يُنسخ يدويًا في كل مرة.
 */
export function Meetings() {
  const queryClient = useQueryClient();
  const profile = useTenantProfile();

  const [provider, setProvider] = useState<MeetingProvider>('ONSITE');
  const [roomUrl, setRoomUrl] = useState('');
  const [minutes, setMinutes] = useState('30');
  const [reminder, setReminder] = useState('15');
  const [jitsiEnabled, setJitsiEnabled] = useState(true);
  const [jitsiDomain, setJitsiDomain] = useState('meet.jit.si');
  const [dirty, setDirty] = useState(false);

  /*
   * الحالة تُملأ من الخادم مرة واحدة عند وصولها.
   *
   * المزامنة في كل تصيير كانت ستمسح ما يكتبه المستخدم عند أي إعادة جلب
   * تقع في الخلفية.
   */
  useEffect(() => {
    const s = profile.data?.settings;
    if (!s || dirty) return;
    setProvider((s.defaultMeetingProvider as MeetingProvider) ?? 'ONSITE');
    setRoomUrl(s.meetingRoomUrl ?? '');
    setMinutes(String(s.defaultMeetingMinutes ?? 30));
    setReminder(String(s.meetingReminderMinutes ?? 15));
    setJitsiEnabled(s.jitsiEnabled ?? true);
    setJitsiDomain(s.jitsiDomain ?? 'meet.jit.si');
  }, [profile.data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      api('/settings/preferences', {
        method: 'PATCH',
        body: {
          defaultMeetingProvider: provider,
          meetingRoomUrl: roomUrl.trim(),
          defaultMeetingMinutes: Number(minutes) || 30,
          meetingReminderMinutes: Number(reminder) || 0,
          jitsiEnabled,
          jitsiDomain: jitsiDomain.trim() || 'meet.jit.si',
        },
      }),
    /*
     * الإبطال قبل رفع علم النظافة.
     *
     * الترتيب المعكوس كان يترك المؤثِّر يعيد المزامنة من نسخة مخزّنة قديمة
     * فيمحو ما حُفظ للتوّ: يختار المستخدم Zoom ويحفظ فتعود الشاشة إلى
     * "حضوري" وكأن الحفظ لم يقع — وقد وقع فعلًا.
     */
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      setDirty(false);
    },
  });

  const touch = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setDirty(true);
  };

  if (profile.isError) {
    return <ErrorState error={profile.error} onRetry={() => profile.refetch()} />;
  }

  const online = provider !== 'ONSITE' && provider !== 'PHONE';

  return (
    <div className="page">
      <PageHead title="الاجتماعات" subtitle="ما يُقترح تلقائيًا عند حجز موعد" />

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">الافتراضيات</h2>
        </div>
        <div className="card-body">
          {save.isSuccess && !dirty && (
            <div className="alert alert-info mb-4">
              <Icon name="check" size={15} />
              <div>حُفظت الإعدادات. تسري على الاجتماعات الجديدة.</div>
            </div>
          )}

          {save.isError && (
            <div className="alert alert-danger mb-4">
              {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
            </div>
          )}

          <div className="form-grid">
            <div className="field span-2">
              <label className="label" htmlFor="m-provider">
                نوع الاجتماع الافتراضي
              </label>
              <select
                id="m-provider"
                className="select"
                value={provider}
                onChange={(e) => touch(setProvider)(e.target.value as MeetingProvider)}
              >
                {MEETING_PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {MEETING_PROVIDER_LABELS[p]}
                  </option>
                ))}
              </select>
              <span className="tiny mute">
                يُختار مسبقًا عند حجز موعد جديد، ويبقى تغييره ممكنًا في كل موعد.
              </span>
            </div>

            {online && (
              <div className="field span-2">
                <label className="label" htmlFor="m-room">
                  رابط غرفة الاجتماع الدائمة
                </label>
                <input
                  id="m-room"
                  className="input num"
                  dir="ltr"
                  placeholder="https://..."
                  value={roomUrl}
                  onChange={(e) => touch(setRoomUrl)(e.target.value)}
                />
                <span className="tiny mute">
                  يُدرَج تلقائيًا في الاجتماعات من هذا النوع، فلا يُنسخ يدويًا في كل مرة.
                  اتركه فارغًا لكتابة رابط مختلف في كل موعد.
                </span>
              </div>
            )}

            <div className="field">
              <label className="label" htmlFor="m-minutes">
                المدة الافتراضية بالدقائق
              </label>
              <input
                id="m-minutes"
                className="input num"
                dir="ltr"
                inputMode="numeric"
                value={minutes}
                onChange={(e) => touch(setMinutes)(e.target.value)}
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="m-reminder">
                التذكير قبل الموعد بالدقائق
              </label>
              <input
                id="m-reminder"
                className="input num"
                dir="ltr"
                inputMode="numeric"
                value={reminder}
                onChange={(e) => touch(setReminder)(e.target.value)}
              />
              <span className="tiny mute">صفر يعني بلا تذكير.</span>
            </div>
          </div>

          <button
            className="btn btn-primary mt-4"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-head">
          <h2 className="card-title">
            <Icon name="camera" size={16} /> الاجتماعات الداخليّة (Jitsi)
          </h2>
        </div>
        <div className="card-body">
          <p className="mute" style={{ marginTop: 0 }}>
            بديلٌ حقيقيٌّ يعمل داخلَ النظام: اختر «اجتماع داخلي (Jitsi)» عند حجزِ موعدٍ فتُولَّد
            غرفةٌ تلقائيًّا وتُفتح داخلَ النظامِ دون أداةٍ خارجيّة. المضيفُ يتحكّمُ في كلِّ شيء،
            والضيفُ في صوته وصورته، ويمكنُ للمضيفِ تسجيلَ الاجتماعِ فيُلحَق تلقائيًّا بقائمةِ
            الاجتماعاتِ للمشاهدة.
          </p>

          <label className="row" style={{ gap: 'var(--sp-2)', alignItems: 'center', marginTop: 'var(--sp-3)' }}>
            <input
              type="checkbox"
              checked={jitsiEnabled}
              onChange={(e) => touch(setJitsiEnabled)(e.target.checked)}
            />
            <span>تفعيلُ الاجتماعاتِ الداخليّةِ عبر Jitsi</span>
          </label>

          {jitsiEnabled && (
            <div className="field span-2 mt-3">
              <label className="label" htmlFor="m-jitsi-domain">
                نطاقُ خادمِ Jitsi
              </label>
              <input
                id="m-jitsi-domain"
                className="input num"
                dir="ltr"
                placeholder="meet.jit.si"
                value={jitsiDomain}
                onChange={(e) => touch(setJitsiDomain)(e.target.value)}
              />
              <span className="tiny mute">
                الافتراضيُّ <span className="num" dir="ltr">meet.jit.si</span> (عامّ). للتحكّمِ
                الكاملِ في الأدوارِ والتسجيلِ على الخادمِ يمكنُ لاحقًا توجيهُه إلى خادمِ Jitsi
                خاصٍّ بالمؤسّسة.
              </span>
            </div>
          )}

          <button
            className="btn btn-primary mt-4"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-head">
          <h2 className="card-title">عن التكامل مع مزوّدي الاجتماعات الخارجيّين</h2>
        </div>
        <div className="card-body">
          {/*
            التصريح بما لا يفعله النظام أنفع من السكوت عنه.
            من يقرأ "Zoom" في قائمة يفترض أن الرابط يُنشأ تلقائيًا، فينتظر
            ما لا يأتي.
          */}
          <p className="mute">
            النظام لا ينشئ روابط اجتماعات تلقائيًا في Zoom أو Google Meet أو Teams — تبقى خياراتٍ
            للتصنيفِ ولصقِ الرابط. أمّا Jitsi فيعمل داخلَ النظامِ فعلًا كما هو موضّحٌ أعلاه.
          </p>
          <p className="mute mt-3">
            الطريقة العملية مع المزوّدين الخارجيّين: استعمل غرفة الاجتماع الدائمة في حسابك لدى
            المزوّد وضع رابطها أعلاه، فيُدرَج في كل موعد بلا خطوة إضافية. أو اترك الحقل فارغًا
            وألصق رابطًا مختلفًا في كل موعد على حدة.
          </p>
        </div>
      </div>
    </div>
  );
}
